import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import displayError from '../../utils/ErrorHandler';
import authenticate from '../../utils/Authenticate';
import UserContext from '../../contexts/UserContext';

const SilatDashboard = () => {
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const {user,setUser} = useContext(UserContext);
  
  const navigate = useNavigate();

  // 🛡️ Auth check
  useEffect(() => {
    async function startup() {
      if (!user) {
        await authenticate(setUser, navigate);
      }
    }
    startup();
  }, [user, setUser, navigate]);

  // 📦 Load items on mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [itemRes, commitRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URI}/api/silatItem/getAll`, { withCredentials: true }),
          axios.get(`${process.env.REACT_APP_API_URI}/api/commit/getAll`, { withCredentials: true })
        ]);
  
        const itemsData = itemRes.data.data;
        const commitData = commitRes.data.data;
  
        // Map commits by itemId for quick lookup
        const commitMap = {};
        commitData.forEach(commit => {
          if (!commitMap[commit.silatItem]) {
            commitMap[commit.silatItem] = 0;
          }
          commitMap[commit.silatItem] += commit.quantity || 0;
        });
  
        const updatedItems = itemsData.map(item => {
          const committedQty = commitMap[item._id] || 0;
          const pendingQty = Math.max(item.requiredQuantity - committedQty, 0); // avoid negatives
          return {
            ...item,
            pendingQuantity: pendingQty
          };
        });
  
        setItems(updatedItems);
      } catch (err) {
        const errorMessage = displayError(err);
        if (errorMessage) setMessage(errorMessage);
      } finally {
        setLoading(false);
      }
    };
  
    loadData();
  }, []);
  

  // 🔍 Filter logic
  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold text-fmb mb-4">Silat Dashboard</h1>

      {message && (
        <div className="text-red-500 text-center mb-4">{message}</div>
      )}

      <input
        type="text"
        placeholder="Search items..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="w-full p-2 mb-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-fmb"
      />

      {loading ? (
        <div className="flex justify-center items-center h-[60vh]">
          <p>Loading, please wait...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="flex justify-center items-center h-[60vh]">
          <p>No items found...</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border border-gray-200 bg-white shadow-md rounded-lg">
            <thead className="bg-fmb text-white">
              <tr>
                <th className="text-left p-3">Item Name</th>
                <th className="text-left p-3">Required Qty</th>
                <th className="text-left p-3">Unit</th>
                <th className="text-left p-3">Price/Unit (₹)</th>
                <th className="text-left p-3">Pending Qty</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((item) => (
                <tr key={item._id} className="border-t border-gray-200 hover:bg-gray-50">
                  <td className="p-3 font-medium text-fmb-dark">{item.name}</td>
                  <td className="p-3">{item.requiredQuantity}</td>
                  <td className="p-3">{item.unit}</td>
                  <td className="p-3">₹{item.price}</td>
                  <td className="p-3">{item.pendingQuantity || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SilatDashboard;